import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Play, Upload, Download, Save } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { FieldInput, FieldSelect } from "../components/ui/Field";

interface Group {
  vendorId: number;
  materialNumber: string;
  plantId: number;
  months: Record<number, { demand: number; capacity: number | null; utilization: number | null; monthLabel: string }>;
}

/** Vendor Capacity Capability — 18 rolling months. Only supplier users may
 * edit (isSupplier flag returned by the API drives editability, matching
 * the source design's SBD-read-only rule). */
export function Vcc() {
  const [vendorId, setVendorId] = useState("");
  const [materialNumber, setMaterialNumber] = useState("");
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["vcc", vendorId, materialNumber],
    queryFn: () => api.get<{ groups: Group[]; isSupplier: boolean }>(`/vcc?${new URLSearchParams({ ...(vendorId && { vendorId }), ...(materialNumber && { materialNumber }) })}`),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: { vendorId: number; materialNumber: string; plantId: number; monthIndex: number; capacityQty?: number; capacityUtilizationPct?: number }) => api.post("/vcc/capacity", payload),
    onSuccess: () => {
      toast("Capacity data saved");
      qc.invalidateQueries({ queryKey: ["vcc"] });
    },
    onError: () => toast("Save failed", "error"),
  });

  const groups = query.data?.groups ?? [];
  const isSupplier = query.data?.isSupplier ?? false;
  const months = Array.from({ length: 18 }, (_, i) => i + 1);

  return (
    <div>
      <Card className="mb-5">
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <FieldInput label="Vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} placeholder="Vendor ID" />
          <FieldInput label="Material Number" value={materialNumber} onChange={(e) => setMaterialNumber(e.target.value)} />
          <FieldSelect label="ERP ID" value="SAP_C11" disabled options={[{ value: "SAP_C11", label: "SAP_C11" }]} />
        </div>
        <div className="px-5 pb-5 flex justify-end">
          <Button icon={Play} onClick={() => query.refetch()}>
            Execute
          </Button>
        </div>
      </Card>

      {groups.map((g) => (
        <Card key={`${g.vendorId}-${g.materialNumber}-${g.plantId}`} className="mb-4">
          <div className="p-4 font-semibold text-sm border-b border-slate-100 dark:border-white/10">
            {g.materialNumber} (Vendor {g.vendorId}, Plant {g.plantId})
          </div>
          <div className="overflow-x-auto">
            <table className="data w-full text-xs">
              <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
                <tr>
                  <th className="p-2 text-left sticky left-0 bg-slate-50 dark:bg-surface-1">Metric</th>
                  {months.map((m) => (
                    <th key={m} className="p-2 text-right">
                      {g.months[m]?.monthLabel ?? m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-100 dark:border-white/5">
                  <td className="p-2 sticky left-0 bg-white dark:bg-surface-2">Demand</td>
                  {months.map((m) => (
                    <td key={m} className="p-2 text-right">
                      {g.months[m]?.demand ?? 0}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-slate-100 dark:border-white/5">
                  <td className="p-2 sticky left-0 bg-white dark:bg-surface-2">Capacity</td>
                  {months.map((m) => (
                    <td key={m} className="p-2 text-right">
                      {isSupplier ? (
                        <input
                          className="w-16 text-right text-xs border border-slate-200 dark:border-white/10 rounded px-1 py-0.5 bg-white dark:bg-white/5"
                          defaultValue={g.months[m]?.capacity ?? ""}
                          onBlur={(e) => saveMutation.mutate({ vendorId: g.vendorId, materialNumber: g.materialNumber, plantId: g.plantId, monthIndex: m, capacityQty: Number(e.target.value) })}
                        />
                      ) : (
                        (g.months[m]?.capacity ?? "—")
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      <div className="flex justify-end gap-2 mt-2">
        {isSupplier && (
          <Button variant="outline" icon={Upload} onClick={() => toast("Excel upload dialog would open here")}>
            Upload Excel
          </Button>
        )}
        <Button variant="outline" icon={Download} onClick={() => api.download("/vcc/export", "Vendor_Capacity_Result.xlsx")}>
          Download Excel
        </Button>
        {isSupplier && (
          <Button icon={Save} onClick={() => toast("Capacity data saved")}>
            Save
          </Button>
        )}
      </div>
    </div>
  );
}
